<template>
	<div class="view-account">
		<Modal
			:show="showViewAccount"
			content_class="col-12 col-lg-9 col-xl-8 confirm-modal"
		>
			<template slot="content" v-if="selectClientTransactions">
				<div class="bg-gradient-to-br from-gray-50 to-white rounded-xl">
					<!-- Header -->
					<div
						class="flex justify-between items-center border-b border-gray-200 pb-4 mb-6 px-6 pt-6"
					>
						<div class="flex items-center gap-3">
							<div
								class="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md"
							>
								<i class="fa fa-receipt text-white text-lg"></i>
							</div>
							<div>
								<h2 class="text-xl font-bold text-gray-800">
									Account Transactions
								</h2>
								<p class="text-xs text-gray-500 mt-0.5">
									{{ selectClientTransactions.length }}
									transaction(s)
								</p>
							</div>
						</div>
						<button
							class="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-all duration-200 hover:rotate-90"
							@click="$store.commit('showViewAccount', false)"
						>
							<i class="fa fa-times text-lg"></i>
						</button>
					</div>

					<!-- Transaction List -->
					<div
						class="px-6 pb-6 max-h-[70vh] overflow-y-auto space-y-4"
					>
						<div
							v-for="transaction in selectClientTransactions"
							:key="transaction._id"
							class="bg-white shadow-lg hover:shadow-xl rounded-xl border border-gray-200 overflow-hidden transition-all duration-300 hover:-translate-y-1"
						>
							<!-- Transaction Header -->
							<div
								class="bg-gradient-to-r from-gray-50 to-white px-5 py-4 border-b border-gray-200"
							>
								<div class="flex justify-between items-center">
									<div class="flex items-center gap-3">
										<div
											class="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm"
											:class="{
												'bg-brand-teal':
													transaction.transactionType.toLowerCase() ===
													'credit',
												'bg-gradient-to-br from-red-500 to-red-600':
													transaction.transactionType.toLowerCase() ===
													'debit',
												'bg-gradient-to-br from-blue-500 to-blue-600':
													![
														'credit',
														'debit',
													].includes(
														transaction.transactionType.toLowerCase(),
													),
											}"
										>
											<i
												class="text-white"
												:class="{
													'fas fa-arrow-down':
														transaction.transactionType.toLowerCase() ===
														'credit',
													'fas fa-arrow-up':
														transaction.transactionType.toLowerCase() ===
														'debit',
													'fas fa-exchange-alt': ![
														'credit',
														'debit',
													].includes(
														transaction.transactionType.toLowerCase(),
													),
												}"
											></i>
										</div>
										<div>
											<div
												class="font-bold text-gray-800 text-base"
											>
												{{
													transaction.transactionType.toUpperCase()
												}}
											</div>
											<div
												class="text-sm text-gray-500 mt-0.5"
											>
												{{
													moment(
														transaction.transactionDate,
													).format('DD/MM/YYYY HH:mm')
												}}
											</div>
										</div>
									</div>
									<div class="flex flex-col items-end gap-2">
										<span
											class="px-3 py-1.5 text-xs font-semibold rounded-full shadow-sm"
											:class="{
												'bg-green-100 text-brand-teal ring-1 ring-emerald-200':
													transaction.transactionStatus ===
													'POSTED',
												'bg-amber-100 text-amber-700 ring-1 ring-amber-200':
													transaction.transactionStatus ===
													'PENDING',
												'bg-red-100 text-red-700 ring-1 ring-red-200':
													transaction.transactionStatus ===
													'FAILED',
											}"
										>
											{{ transaction.transactionStatus }}
										</span>
										<div
											class="text-lg font-bold"
											:class="{
												'text-brand-teal':
													transaction.transactionType.toLowerCase() ===
													'credit',
												'text-red-600':
													transaction.transactionType.toLowerCase() ===
													'debit',
												'text-blue-600': ![
													'credit',
													'debit',
												].includes(
													transaction.transactionType.toLowerCase(),
												),
											}"
										>
											{{
												transaction.transactionCurrency
											}}
											{{
												transaction.transactionAmount.toLocaleString()
											}}
										</div>
									</div>
								</div>
							</div>

							<!-- Transaction Details -->
							<div class="px-5 py-5">
								<div
									class="grid grid-cols-1 md:grid-cols-2 gap-4"
								>
									<!-- Transaction ID -->
									<div class="group">
										<div
											class="flex items-center gap-2 mb-1.5"
										>
											<div
												class="w-1.5 h-1.5 rounded-full bg-blue-500"
											></div>
											<span
												class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
												>Transaction ID</span
											>
										</div>
										<div
											class="text-sm text-gray-800 font-mono bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 group-hover:border-blue-300 transition-colors"
										>
											{{ transaction.transactionId }}
										</div>
									</div>

									<!-- Description -->
									<div class="group">
										<div
											class="flex items-center gap-2 mb-1.5"
										>
											<div
												class="w-1.5 h-1.5 rounded-full bg-purple-500"
											></div>
											<span
												class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
												>Description</span
											>
										</div>
										<div
											class="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 group-hover:border-purple-300 transition-colors"
										>
											{{
												transaction.transactionDescription
											}}
										</div>
									</div>

									<!-- Category -->
									<div class="group md:col-span-2">
										<div
											class="flex items-center gap-2 mb-1.5"
										>
											<div
												class="w-1.5 h-1.5 rounded-full bg-orange-500"
											></div>
											<span
												class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
												>Category</span
											>
										</div>
										<div class="flex flex-wrap gap-2">
											<span
												class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-brand-orange bg-red-50 rounded-lg border border-orange-200"
											>
												<i
													class="fas fa-tag mr-2 text-xs"
												></i>
												{{
													transaction.transactionCategory
												}}
											</span>
										</div>
									</div>
								</div>
							</div>

							<!-- Accent Bar -->
							<div
								class="h-1.5 w-full"
								:class="{
									'bg-brand-teal':
										transaction.transactionType.toLowerCase() ===
										'credit',
									'bg-brand-crimson':
										transaction.transactionType.toLowerCase() ===
										'debit',
									'bg-gradient-to-r from-blue-400 to-blue-500':
										!['credit', 'debit'].includes(
											transaction.transactionType.toLowerCase(),
										),
								}"
							></div>
						</div>

						<!-- Empty State -->
						<div
							v-if="
								!selectClientTransactions ||
								selectClientTransactions.length === 0
							"
							class="text-center py-12"
						>
							<div
								class="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4"
							>
								<i
									class="fas fa-receipt text-3xl text-gray-400"
								></i>
							</div>
							<h3
								class="text-lg font-semibold text-gray-700 mb-2"
							>
								No Transactions Found
							</h3>
							<p class="text-sm text-gray-500">
								There are no transactions to display at this
								time.
							</p>
						</div>
					</div>
				</div>
			</template>
		</Modal>
	</div>
</template>

<script>
import VueScrollbar from 'vue-scrollbar-simple';

export default {
	components: {
		Form: () => import('@/libs/clarks/components/form'),
		Table: () => import('@/libs/clarks/components/table/table'),
		Modal: () => import('@/components/newportal/templates/others/modal'),
		Dropdown: () => import('@/libs/clarks/components/dropdown/dropdown'),
		DropdownItem: () =>
			import('@/libs/clarks/components/dropdown/dropdown-item'),
		DropdownMenu: () =>
			import('@/libs/clarks/components/dropdown/dropdown-menu'),
		scrollbar: VueScrollbar,
	},
	computed: {
		...mapGetters([
			'loading',
			'accountTransactions',
			'selected_account',
			'showViewAccount',
			'AccountsModel',
			'showWithdrawalAccount',
			'showDownloadStatement',
			'withdrwal_page',
			'withdrawal_types',
			'withdrawal_details',
			'selected_withdrawal_type',
		]),
		...mapGetters('invoices', [
			'selectInvoiceDetails',
			'selectClientTransactions',
		]),
	},
	data() {
		return {
			utils,
			moment,
			gateway: process.env.VUE_APP_GATEWAY,
		};
	},
	methods: {
		...mapActions([
			'requestIPF',
			'payIPF',
			'downloadStatement',
			'moveToNextReport',
			'moveToPreviousReport',
			'withdrawFunds',
			'openTabIPF',
		]),
		closeViewAccount(type) {
			if (type === 0) {
				this.$store.commit('', false);
			}
		},
		formatCurrency(value) {
			const formatter = new Intl.NumberFormat(undefined, {
				style: 'currency',
				currency: 'KES',
			});
			return formatter.format(value);
		},
		async clickAction(action) {
			switch (action) {
				case 'withdraw':
					this.$store.commit('showWithdrawalAccount', true);
					break;
				case 'get_statement':
					this.$store.commit('showDownloadStatement', true);
					Event.$emit('loader:show', false);
					break;
				default:
					break;
			}
		},
	},
	async mounted() {},
};
</script>

<style lang="less">
// Brand colors from view-invoice.vue
@brand-magenta: #e91e63;
@brand-orange: #ff9800;
@brand-teal: #1db584;
@brand-blue: #2196f3;
@brand-red: #f44336;
@brand-green: #4caf50;

// Professional neutrals
@white: #ffffff;
@grey-50: #fafafa;
@grey-100: #f5f5f5;
@grey-200: #eeeeee;
@grey-400: #bdbdbd;
@grey-600: #757575;
@grey-700: #616161;
@grey-800: #424242;
@grey-900: #212121;

// Text colors
@text-primary: #212121;
@text-secondary: #757575;
@text-muted: #9e9e9e;

.view-account {
	.tx-green {
		color: @brand-green;
	}
	.tx-orange {
		color: @brand-orange;
	}
	.tx-primary {
		color: @brand-blue;
	}
	.tx-red {
		color: @brand-red;
	}

	.account-box {
		position: relative;
		border-radius: 12px;
		border: 1px solid @grey-200;
		background: @white;
		margin: 16px 0;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
		transition: all 0.3s ease;
		overflow: hidden;

		&:hover {
			transform: translateY(-2px);
			box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
			border-color: @brand-teal;

			.invoice-icon {
				color: @brand-magenta;
				transform: scale(1.1);
			}

			.detail-icon {
				color: @brand-orange;
				transform: scale(1.1);
			}

			.accent-bar {
				width: 100%;
			}
		}

		.account-header {
			display: flex;
			align-items: center;
			padding: 24px 24px 0 24px;
			margin-bottom: 16px;

			.account-type {
				display: flex;
				align-items: center;
				gap: 10px;

				.invoice-icon {
					font-size: 20px;
					color: @brand-teal;
					transition: all 0.3s ease;
				}

				.type-text {
					font-size: 18px;
					font-weight: 700;
					color: @text-primary;
					letter-spacing: 0.5px;
				}
			}
		}

		.account-content {
			padding: 0 24px 24px 24px;
			display: flex;
			justify-content: space-between;
			align-items: flex-start;
		}

		.account-info {
			flex: 1;
			margin-right: 24px;

			.ref-section {
				margin-bottom: 20px;

				.ref-number {
					font-size: 16px;
					font-weight: 700;
					color: @brand-blue;
					background: rgba(33, 150, 243, 0.1);
					padding: 8px 12px;
					border-radius: 6px;
					display: inline-block;
				}
			}

			.account-details {
				display: flex;
				flex-direction: column;
				gap: 12px;

				.detail-item {
					display: flex;
					align-items: center;
					gap: 10px;

					.detail-icon {
						font-size: 14px;
						color: @brand-orange;
						width: 16px;
						transition: all 0.3s ease;
					}

					.detail-label {
						font-size: 14px;
						color: @text-secondary;
						font-weight: 500;
						min-width: 80px;
					}

					.detail-value {
						font-size: 14px;
						color: @text-primary;
						font-weight: 600;

						&.balance-amount {
							color: @brand-blue;
						}
					}
				}
			}
		}

		.action-section {
			text-align: right;
			min-width: 200px;

			.action-button {
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 10px;
				padding: 12px 24px;
				background: @brand-green;
				color: @white;
				border-radius: 8px;
				cursor: pointer;
				transition: all 0.3s ease;
				font-weight: 600;
				box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);

				&:hover {
					transform: translateY(-2px);
					box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
					background: darken(@brand-green, 5%);
				}

				.btn-icon {
					font-size: 14px;
				}

				.btn-text {
					font-size: 14px;
				}

				.lds-ring {
					display: inline-block;
					margin-left: 8px;
				}
			}
		}

		.accent-bar {
			position: absolute;
			bottom: 0;
			left: 0;
			height: 4px;
			width: 60px;
			background: @brand-teal;
			transition: all 0.4s ease;
		}
	}

	.transactions-card {
		border-radius: 12px;
		border: 1px solid @grey-200;
		background: @white;
		margin: 20px 0;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
		overflow: hidden;

		.card-header {
			padding: 20px 24px;
			background: @grey-50;
			border-bottom: 1px solid @grey-200;
			display: flex;
			align-items: center;
			gap: 10px;

			i {
				color: @brand-blue;
				font-size: 16px;
			}

			span {
				font-size: 16px;
				font-weight: 600;
				color: @text-primary;
			}
		}

		.transactions-table {
			padding: 16px 24px;
		}
	}

	.content {
		.steps {
			.forms {
				.h4 {
					font-weight: 600;
					font-size: 13px;
					text-transform: uppercase;
					color: @brand-blue;
					margin: 5px 0 0 0;
				}

				.txt-account {
					font-size: 14px;
					margin: 10px 0;
				}

				.page-box {
					.card {
						border-radius: 12px;
						border: 1px solid @grey-200;
						background: @white;
						box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
					}

					.scroll-content {
						.select-card {
							cursor: pointer;
							border-radius: 8px;
							margin: 8px 0;
							padding: 16px;
							transition: all 0.3s ease;

							&.selected {
								background-color: rgba(220, 220, 220, 0.3);
							}

							.image {
								max-width: 90px;
								max-height: 90px;
								width: auto;
								height: auto;
								margin: 5px 15px 0 0;
							}

							.amount {
								font-weight: 600;
								font-size: 14px;
								color: @text-primary;
							}

							.name {
								font-size: 12px;
								font-weight: 600;
								color: @text-secondary;
							}
						}
					}
				}
			}

			.button-actions {
				margin: 20px 0;

				.action-button {
					display: flex;
					align-items: center;
					justify-content: center;
					gap: 10px;
					padding: 12px 24px;
					background: @brand-green;
					color: @white;
					border-radius: 8px;
					cursor: pointer;
					transition: all 0.3s ease;
					font-weight: 600;
					box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);

					&:hover {
						transform: translateY(-2px);
						box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
						background: darken(@brand-green, 5%);
					}

					&[disabled] {
						background: @grey-400;
						cursor: not-allowed;
						box-shadow: none;
					}

					.btn-text {
						font-size: 14px;
					}

					.lds-ring {
						display: inline-block;
						margin-left: 8px;
					}
				}
			}

			.confirm-forms {
				border-radius: 12px;
				border: 1px solid @grey-200;
				background: @white;
				padding: 16px 24px;
				box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

				.info-text {
					font-weight: 600;
					font-size: 13px;
					color: @text-primary;

					i {
						color: @brand-blue;
						margin-right: 8px;
					}
				}
			}
		}
	}

	// Responsive design
	@media (max-width: 768px) {
		.account-box {
			.account-content {
				flex-direction: column;
				gap: 20px;

				.account-info {
					margin-right: 0;
				}

				.action-section {
					text-align: left;
					min-width: auto;
				}
			}
		}

		.transactions-card {
			.transactions-table {
				padding: 12px 16px;
			}
		}

		.content .steps .button-actions .action-button {
			padding: 10px 16px;
		}
	}

	// Custom scrollbar
	::-webkit-scrollbar {
		width: 8px;
	}

	::-webkit-scrollbar-track {
		background: @grey-100;
		border-radius: 4px;
	}

	::-webkit-scrollbar-thumb {
		background: @grey-400;
		border-radius: 4px;

		&:hover {
			background: @grey-600;
		}
	}
}
</style>
